import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { FileImage } from '../../components/ui/ApiImage';
import { ClipboardImageIcon } from '../../components/ui/icons';
import { Hint } from '../../components/ui/Hint';
import { ImageCropDialog } from '../../components/ui/ImageCropDialog';
import { useAuth } from '../auth/AuthContext';
import { uploadFile } from '../locations/api';
import {
  attachComponentImage,
  detachComponentImage,
  listComponentImages,
  listPackageImages,
  setPrimaryComponentImage,
} from './api';

interface Props {
  componentId: string;
  packageId?: string | null;
}

/**
 * Photographs of a part.
 *
 * Thumbnails, opened full size on click — a card that loads six full-resolution
 * photographs to show them at 90 pixels wide is slow for no benefit.
 *
 * Uploading is two steps under the hood: the file is stored content-addressed
 * and then linked. That is what makes the same photograph attached to two parts
 * cost one copy on disk.
 */
export function ComponentImages({ componentId, packageId }: Props) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [cropping, setCropping] = useState<File | null>(null);

  const canEdit = can('catalog.write');

  const images = useQuery({
    queryKey: ['components', componentId, 'images'],
    queryFn: () => listComponentImages(componentId),
  });
  const packageImages = useQuery({
    queryKey: ['reference', 'packages', packageId, 'images'],
    queryFn: () => listPackageImages(packageId!),
    enabled: Boolean(packageId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['components', componentId, 'images'] });
  };

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) {
        const stored = await uploadFile(file);
        await attachComponentImage(componentId, stored.id);
      }
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const makePrimary = useMutation({
    mutationFn: (imageId: string) => setPrimaryComponentImage(componentId, imageId),
    onSuccess: refresh,
  });
  const detach = useMutation({
    mutationFn: (imageId: string) => detachComponentImage(componentId, imageId),
    onSuccess: refresh,
  });

  const paste = async () => {
    try {
      const items = await navigator.clipboard.read();
      const files = await Promise.all(
        items.flatMap((item) =>
          item.types
            .filter((type) => type.startsWith('image/'))
            .map(async (type) => new File([await item.getType(type)], 'clipboard-image.png', { type })),
        ),
      );
      if (files.length) upload.mutate(files);
    } catch {
      setError('clipboard_image_unavailable');
    }
  };

  const rows = images.data ?? [];

  return (
    <section
      className={`gallery${dragging ? ' gallery--dropping' : ''}`}
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        setDragging(false);
        const files = Array.from(event.dataTransfer.files).filter((file) =>
          file.type.startsWith('image/'),
        );
        if (files.length) upload.mutate(files);
      }}
    >
      {/* Everything on the title's own line. The gallery stands in a column
          beside the parameters now, and a full-width row that said "Photographs"
          on the left and "Add photo" on the right was most of that row's width
          spent on two words. The instructions — drag, or paste from the
          clipboard — went the same way: they are for the first time, and a
          sentence that repeats itself on every card is a sentence nobody reads
          twice. */}
      <header className="gallery__head">
        <h3>{t('components.photos')}</h3>
        {canEdit && (
          <>
            <Hint text={t('components.dropOrPasteHint')} />
            <button
              type="button"
              className="gallery__add"
              onClick={() => input.current?.click()}
              disabled={upload.isPending}
              title={t('components.addPhoto')}
              aria-label={t('components.addPhoto')}
            >
              {upload.isPending ? '…' : '+'}
            </button>
            <button type="button" className="gallery__add" onClick={() => void paste()} disabled={upload.isPending} title={t('components.pasteImage')} aria-label={t('components.pasteImage')}><ClipboardImageIcon /></button>
            <button type="button" className="gallery__add" onClick={() => camera.current?.click()} disabled={upload.isPending} title={t('images.takePhoto')} aria-label={t('images.takePhoto')}>{String.fromCharCode(8961)}</button>
          </>
        )}
      </header>

      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length) upload.mutate(files);
          event.target.value = '';
        }}
      />

      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={(event) => {
        const file = event.target.files?.[0];
        if (file) setCropping(file);
        event.target.value = '';
      }} />

      {error && (
        <p className="panel__error">
          {t(`components.errors.${error}`, t('components.errors.unknown'))}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="panel__hint">{t('components.noPhotos')}</p>
      ) : (
        <ul className="gallery__grid">
          {rows.map((image) => (
            <li key={image.id}>
              <button type="button" onClick={() => setViewing(image.file_id)}>
                <FileImage
                  fileId={image.file_id}
                  variant="thumb"
                  alt={image.caption ?? t('components.photo')}
                  loading="lazy"
                />
              </button>
              {image.is_primary && <span className="gallery__primary">★</span>}
              {canEdit && (
                <span className="gallery__actions">
                  {!image.is_primary && (
                    <button type="button" onClick={() => makePrimary.mutate(image.id)}>
                      {t('components.makePrimary')}
                    </button>
                  )}
                  <button type="button" onClick={() => detach.mutate(image.id)}>
                    {t('components.removePhoto')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {(packageImages.data ?? []).length > 0 && (
        <><h4>{t('packages.images')}</h4><ul className="gallery__grid">{packageImages.data!.map((image) => (
          <li key={`package-${image.id}`}><button type="button" onClick={() => setViewing(image.file_id)}><FileImage fileId={image.file_id} variant="thumb" alt={image.caption ?? t('packages.images')} loading="lazy" /></button></li>
        ))}</ul></>
      )}

      {cropping && <ImageCropDialog file={cropping} onCancel={() => setCropping(null)} onReady={(file) => { setCropping(null); upload.mutate([file]); }} />}

      {viewing && (
        <div
          className="viewer"
          role="button"
          tabIndex={0}
          onClick={() => setViewing(null)}
          onKeyDown={(event) => event.key === 'Escape' && setViewing(null)}
        >
          <FileImage fileId={viewing} variant="lg" alt={t('components.photo')} />
        </div>
      )}
    </section>
  );
}
